// InputHandler class for capturing user input on both desktop and mobile
// Requirements: 4.1, 4.2

import { InputEvent, InputType } from '../shared/types';

export interface InputHandlerCallbacks {
  onJumpInput?: (inputEvent: InputEvent) => void;
  onMoveLeftInput?: (inputEvent: InputEvent) => void;
  onMoveRightInput?: (inputEvent: InputEvent) => void;
}

export class InputHandler {
  private callbacks: InputHandlerCallbacks = {};
  private isEnabled: boolean = false;
  private keydownHandler: (event: KeyboardEvent) => void;
  private keyupHandler: (event: KeyboardEvent) => void;
  private touchHandler: (event: TouchEvent) => void;
  private clickHandler: (event: MouseEvent) => void;
  private playerId: string | null = null;
  private inputSequence: number = 0;
  private activeKeys: Set<string> = new Set();
  private mobileControlsContainer: HTMLElement | null = null;

  constructor(callbacks: InputHandlerCallbacks = {}) {
    this.callbacks = callbacks;
    
    // Bind event handlers to maintain 'this' context
    this.keydownHandler = this.handleKeydown.bind(this);
    this.keyupHandler = this.handleKeyup.bind(this);
    this.touchHandler = this.handleTouch.bind(this);
    this.clickHandler = this.handleClick.bind(this);
  }

  /**
   * Binds input controls for desktop and mobile
   * Requirements: 4.1, 4.2, 4.3
   */
  bindControls(): void {
    if (this.isEnabled) {
      return; // Already bound
    }

    // Desktop controls - keyboard events
    document.addEventListener('keydown', this.keydownHandler);
    document.addEventListener('keyup', this.keyupHandler);
    
    // Create mobile controls if on mobile device
    if (this.isMobileDevice()) {
      this.createMobileControls();
    } else {
      // Mouse click as alternative input for desktop
      document.addEventListener('click', this.clickHandler);
    }
    
    this.isEnabled = true;
    console.log('Input controls bound');
  }

  /**
   * Unbinds input controls
   */
  unbindControls(): void {
    if (!this.isEnabled) {
      return; // Already unbound
    }

    document.removeEventListener('keydown', this.keydownHandler);
    document.removeEventListener('keyup', this.keyupHandler);
    document.removeEventListener('touchstart', this.touchHandler);
    document.removeEventListener('click', this.clickHandler);
    
    // Remove mobile controls if they exist
    if (this.mobileControlsContainer) {
      this.mobileControlsContainer.remove();
      this.mobileControlsContainer = null;
    }
    
    this.activeKeys.clear();
    this.isEnabled = false;
    console.log('Input controls unbound');
  }

  /**
   * Updates the callback functions
   */
  setCallbacks(callbacks: InputHandlerCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Gets the current enabled state
   */
  isInputEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Sets the player ID for input event creation
   * Requirements: 4.1, 4.2
   */
  setPlayerId(playerId: string): void {
    this.playerId = playerId;
  }

  /**
   * Gets the current player ID
   */
  getPlayerId(): string | null {
    return this.playerId;
  }

  /**
   * Handles keyboard input events
   */
  private handleKeydown(event: KeyboardEvent): void {
    // Prevent repeat events for held keys
    if (event.repeat) {
      return;
    }

    // Check for jump keys: spacebar or up arrow
    if (event.code === 'Space' || event.code === 'ArrowUp') {
      event.preventDefault();
      this.triggerJumpInput();
    }
    // Check for move left keys: A or left arrow
    else if (event.code === 'KeyA' || event.code === 'ArrowLeft') {
      event.preventDefault();
      if (!this.activeKeys.has(event.code)) {
        this.activeKeys.add(event.code);
        this.triggerMoveLeftInput();
      }
    }
    // Check for move right keys: D or right arrow
    else if (event.code === 'KeyD' || event.code === 'ArrowRight') {
      event.preventDefault();
      if (!this.activeKeys.has(event.code)) {
        this.activeKeys.add(event.code);
        this.triggerMoveRightInput();
      }
    }
  }

  /**
   * Handles keyboard key release events
   */
  private handleKeyup(event: KeyboardEvent): void {
    // Remove key from active set when released
    if (event.code === 'KeyA' || event.code === 'ArrowLeft' || 
        event.code === 'KeyD' || event.code === 'ArrowRight') {
      this.activeKeys.delete(event.code);
    }
  }

  /**
   * Handles touch input events for mobile
   */
  private handleTouch(event: TouchEvent): void {
    // Prevent default to avoid scrolling and other touch behaviors
    event.preventDefault();
    this.triggerJumpInput();
  }

  /**
   * Handles mouse click events
   */
  private handleClick(event: MouseEvent): void {
    // Only handle left mouse button
    if (event.button === 0) {
      event.preventDefault();
      this.triggerJumpInput();
    }
  }

  /**
   * Triggers the jump input callback with proper input event creation
   * Requirements: 4.1, 4.2
   */
  private triggerJumpInput(): void {
    if (this.callbacks.onJumpInput && this.playerId) {
      const inputEvent: InputEvent = {
        playerId: this.playerId,
        type: InputType.JUMP,
        timestamp: Date.now(),
        sequence: ++this.inputSequence
      };
      this.callbacks.onJumpInput(inputEvent);
    }
  }

  /**
   * Triggers the move left input callback
   * Requirements: 4.2, 4.5
   */
  private triggerMoveLeftInput(): void {
    if (this.callbacks.onMoveLeftInput && this.playerId) {
      const inputEvent: InputEvent = {
        playerId: this.playerId,
        type: InputType.MOVE_LEFT,
        timestamp: Date.now(),
        sequence: ++this.inputSequence
      };
      this.callbacks.onMoveLeftInput(inputEvent);
    }
  }

  /**
   * Triggers the move right input callback
   * Requirements: 4.2, 4.5
   */
  private triggerMoveRightInput(): void {
    if (this.callbacks.onMoveRightInput && this.playerId) {
      const inputEvent: InputEvent = {
        playerId: this.playerId,
        type: InputType.MOVE_RIGHT,
        timestamp: Date.now(),
        sequence: ++this.inputSequence
      };
      this.callbacks.onMoveRightInput(inputEvent);
    }
  }

  /**
   * Detects if the device is mobile
   */
  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           ('ontouchstart' in window) ||
           (navigator.maxTouchPoints > 0);
  }

  /**
   * Creates mobile control buttons
   * Requirements: 4.3
   */
  private createMobileControls(): void {
    // Create container for mobile controls
    this.mobileControlsContainer = document.createElement('div');
    this.mobileControlsContainer.id = 'mobile-controls';
    this.mobileControlsContainer.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 0;
      right: 0;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      padding: 0 20px;
      pointer-events: none;
      z-index: 1000;
    `;

    // Create left movement button
    const moveLeftButton = this.createMobileButton('◀', 'Move Left');
    moveLeftButton.style.cssText += `
      margin-right: auto;
    `;
    this.bindMobileButton(moveLeftButton, () => this.triggerMoveLeftInput());

    // Create jump button (center, higher up)
    const jumpButton = this.createMobileButton('▲', 'Jump');
    jumpButton.style.cssText += `
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      bottom: 80px;
      background-color: #4CAF50;
    `;
    this.bindMobileButton(jumpButton, () => this.triggerJumpInput());

    // Create right movement button
    const moveRightButton = this.createMobileButton('▶', 'Move Right');
    moveRightButton.style.cssText += `
      margin-left: auto;
    `;
    this.bindMobileButton(moveRightButton, () => this.triggerMoveRightInput());

    // Add buttons to container
    this.mobileControlsContainer.appendChild(moveLeftButton);
    this.mobileControlsContainer.appendChild(jumpButton);
    this.mobileControlsContainer.appendChild(moveRightButton);

    // Add container to document
    document.body.appendChild(this.mobileControlsContainer);
  }

  /**
   * Creates a mobile button with consistent styling
   */
  private createMobileButton(text: string, label: string): HTMLElement {
    const button = document.createElement('button');
    button.textContent = text;
    button.setAttribute('aria-label', label);
    button.style.cssText = `
      width: 60px;
      height: 60px;
      border-radius: 50%;
      border: 2px solid #333;
      background-color: #2196F3;
      color: white;
      font-size: 24px;
      font-weight: bold;
      cursor: pointer;
      user-select: none;
      touch-action: manipulation;
      pointer-events: auto;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
      transition: all 0.1s ease;
    `;
    return button;
  }

  /**
   * Binds touch events to a mobile button
   */
  private bindMobileButton(button: HTMLElement, callback: () => void): void {
    let isPressed = false;

    const handleStart = (e: Event) => {
      e.preventDefault();
      if (!isPressed) {
        isPressed = true;
        button.style.transform = 'scale(0.95)';
        button.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
        callback();
      }
    };

    const handleEnd = (e: Event) => {
      e.preventDefault();
      if (isPressed) {
        isPressed = false;
        button.style.transform = 'scale(1)';
        button.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
      }
    };

    // Touch events
    button.addEventListener('touchstart', handleStart, { passive: false });
    button.addEventListener('touchend', handleEnd, { passive: false });
    button.addEventListener('touchcancel', handleEnd, { passive: false });

    // Mouse events for testing on desktop
    button.addEventListener('mousedown', handleStart);
    button.addEventListener('mouseup', handleEnd);
    button.addEventListener('mouseleave', handleEnd);
  }
}